/**
 * AdvertiserDashboardSeparatePersonaPage — advertiser persona dashboard.
 *
 * Surfaces a per-advertiser view (persona switcher) with:
 *   - three headline KPI tiles (Spend, Conversions, CPA)
 *   - a persona table listing every persona the tenant has configured
 *
 * Green accent palette only (green-500..800 + zinc). No emerald/blue/orange/
 * purple. No dark mode. No emoji. No competitor names.
 */
import { useMemo, useState } from "react";
import {
	useAdvertiserPersonaList,
	useAdvertiserPersonaDelete,
} from "./hooks";
import {
	aggregateAdvertiserKpis,
	formatKpi,
	selectPersona,
	type AdvertiserKpi,
	type AdvertiserPersonaRow,
} from "./api";

// --- tailwind primitive strings -----------------------------------------
const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_TAB_ON =
	"rounded-[6px] border border-green-700 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-800";
const BTN_TAB_OFF =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-green-600 hover:text-green-700";

function KpiTile({ kpi }: { kpi: AdvertiserKpi }) {
	return (
		<div
			className={`${CARD} flex flex-col gap-1`}
			data-testid={`kpi-tile-${kpi.key}`}
		>
			<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
				{kpi.label}
			</p>
			<p
				className="text-2xl font-semibold text-green-800"
				data-testid={`kpi-value-${kpi.key}`}
			>
				{formatKpi(kpi)}
			</p>
		</div>
	);
}

export function AdvertiserDashboardSeparatePersonaPage() {
	const listQuery = useAdvertiserPersonaList();
	const deleteMut = useAdvertiserPersonaDelete();

	const items: AdvertiserPersonaRow[] = listQuery.data?.items ?? [];

	const [activeAdvertiser, setActiveAdvertiser] = useState<string | null>(
		null,
	);

	const selected = useMemo(
		() => selectPersona(items, activeAdvertiser),
		[items, activeAdvertiser],
	);

	const kpis = useMemo(() => {
		// If a persona is selected, aggregate just that one. Otherwise aggregate
		// across the whole tenant roster.
		const scope = selected ? [selected] : items;
		return aggregateAdvertiserKpis(scope);
	}, [items, selected]);

	return (
		<main
			data-testid="advertiser-dashboard-separate-persona-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					advertiser / persona
				</p>
				<h1 className="bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-3xl font-semibold text-transparent">
					Advertiser Dashboard (Persona View)
				</h1>
				<p className="text-sm text-zinc-600">
					Per-advertiser persona layout and pinned KPIs. Switch personas to
					see that advertiser&apos;s scoped numbers.
				</p>
			</header>

			<section
				className="flex flex-col gap-3"
				aria-label="Persona selector"
				data-testid="persona-switcher"
			>
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Active persona
				</p>
				<div className="flex flex-wrap gap-2">
					<button
						type="button"
						onClick={() => setActiveAdvertiser(null)}
						className={activeAdvertiser === null ? BTN_TAB_ON : BTN_TAB_OFF}
						data-testid="persona-tab-all"
					>
						All advertisers
					</button>
					{items.map((row) => (
						<button
							key={row.advertiser_id}
							type="button"
							onClick={() => setActiveAdvertiser(row.advertiser_id)}
							className={
								activeAdvertiser === row.advertiser_id
									? BTN_TAB_ON
									: BTN_TAB_OFF
							}
							data-testid={`persona-tab-${row.advertiser_id}`}
						>
							{row.advertiser_id}
						</button>
					))}
				</div>
			</section>

			<section aria-label="KPI tiles">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{kpis.map((k) => (
						<KpiTile key={k.key} kpi={k} />
					))}
				</div>
			</section>

			<section className={CARD} aria-label="Persona list">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Configured personas
					</h2>
					{selected ? (
						<span className="text-xs text-zinc-500">
							showing {selected.advertiser_id}
						</span>
					) : (
						<span className="text-xs text-zinc-500">all advertisers</span>
					)}
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-dashboard-separate-persona-loading"
					>
						Loading personas.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-dashboard-separate-persona-empty"
					>
						No personas configured yet.
					</p>
				) : (
					<table
						className="mt-3 w-full text-left text-sm"
						data-testid="advertiser-dashboard-separate-persona-table"
					>
						<thead className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
							<tr>
								<th className="py-2 pr-4 font-medium">Advertiser</th>
								<th className="py-2 pr-4 font-medium">Widgets</th>
								<th className="py-2 pr-4 font-medium">Visible KPIs</th>
								<th className="py-2 pr-4 font-medium">Updated</th>
								<th className="py-2 font-medium text-right">Action</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{items.map((row) => {
								const isActive = row.advertiser_id === activeAdvertiser;
								return (
									<tr
										key={row.id}
										className={isActive ? "bg-green-50" : undefined}
										data-testid={`advertiser-dashboard-separate-persona-row-${row.advertiser_id}`}
									>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-800">
											{row.advertiser_id}
										</td>
										<td className="py-2 pr-4 text-zinc-700">
											{Array.isArray(row.layout) ? row.layout.length : 0}
										</td>
										<td className="py-2 pr-4 text-zinc-700">
											{Array.isArray(row.visible_kpis)
												? row.visible_kpis.length
												: 0}
										</td>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-600">
											{formatTs(row.updated_at)}
										</td>
										<td className="py-2 text-right">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => deleteMut.mutate(row.id)}
												disabled={deleteMut.isPending}
												data-testid={`advertiser-dashboard-separate-persona-delete-${row.advertiser_id}`}
											>
												Remove
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}

				{deleteMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{deleteMut.error.message}
					</p>
				) : null}
			</section>

			<footer className="flex items-center justify-end">
				<button
					type="button"
					className={BTN_PRIMARY}
					onClick={() => listQuery.refetch()}
					disabled={listQuery.isFetching}
					data-testid="advertiser-dashboard-separate-persona-refresh"
				>
					{listQuery.isFetching ? "Refreshing." : "Refresh"}
				</button>
			</footer>
		</main>
	);
}

function formatTs(sec: number): string {
	if (!Number.isFinite(sec) || sec <= 0) return "-";
	const d = new Date(sec * 1000);
	if (Number.isNaN(d.getTime())) return "-";
	return d.toISOString().slice(0, 10);
}

export default AdvertiserDashboardSeparatePersonaPage;
